<script>
    import {router} from "../lib/Router/Router.svelte";
    import R, {LOADER} from "../lib/requests";
    import {APP_VERSION} from "../lib/constants";
    import app from "../stores/app";
    import user from "../stores/user";
    import localTransfers from "../stores/localTransfers";
    import {createEventDispatcher} from 'svelte/internal';
    import {clickOutside} from "../lib/clickOutside";
    import {getDate} from "../lib/lib";
    import AppIcon from "./icons/AppIcon.svelte";
    import A from "../lib/Router/A.svelte";
    import Notifier from "../stores/notifier";

    let showUserDropdown = false;

    const dispatch = createEventDispatcher();

    const handleEscapeKey = event => {
        if (event.key === 'Escape') {
            closeUserDropdown();
        }
    }

    const logOut = () => {
        dispatch("logout");
    }

    const toggleMenu = () => {
        $app.menuExpanded = $app.menuExpanded > 0 ? 0 : 2;
    }

    const toggleUserDropdown = () => {
        if (!showUserDropdown) {
            showUserDropdown = true;
            document.addEventListener('keydown', handleEscapeKey);
        } else {
            closeUserDropdown();
        }
    }

    const closeUserDropdown = () => {
        showUserDropdown = false;
        document.removeEventListener('keydown', handleEscapeKey);
    }

    const toggleNotificationStatus = () => {
        const onSuccess = result => {
            $user.notification_enabled = result;
            Notifier.showMessage($user.notification_enabled == 1 ? 'Notifications are enabled' : 'Notifications have been disabled');
        }

        const onFailure = err => {
        };

        R.handle(R.user.toggleNotificationState, [], onSuccess, onFailure, LOADER.POPUP);
    }
</script>

<header>
    <div class="content">
        <div class="content-left">
            <div class="menu-icon" on:click="{ toggleMenu }">
                <AppIcon icon="menu" size="24"/>
            </div>
            <div class="brand">
                <img src="/assets/images/app-logo.png" alt="logo">
                <div class="version">v { APP_VERSION }</div>
            </div>
        </div>
        <div class="content-right">
            <div class="content-item" title="new reservations">
                <A href="/app/reservations" class="item-button">
                    <AppIcon icon="reservations" size="18"/>
                    {#if $app.new_reservations > 0}
                        <span class="badge">{$app.new_reservations}</span>
                    {/if}
                </A>
            </div>
            {#if $user.notification_type > 0}
                <div class="content-item border-left" title="toggle notifications">
                    <button class="btn-reset item-button" on:click={toggleNotificationStatus}>
                        <AppIcon icon={$user.notification_enabled == 1 ? 'bell-solid' : 'bell-solid-off'} size="18"/>
                    </button>
                </div>
            {/if}
            {#if user.canViewLocalTransfers()}
                <div class="content-item border-left" title="local transfers">
                    <A href="/app/locals" class="item-button">
                        <AppIcon icon="taxi-solid" size="18"/>
                        {#if $localTransfers.new_locals > 0}
                            <span class="badge">{ $localTransfers.new_locals }</span>
                        {/if}
                    </A>
                </div>
            {/if}
            {#if user.canMakeTransfers()}
                <div class="content-item border-left" title="new transfer">
                    <A href="/app/new-transfer" class="item-button">
                        <AppIcon icon="plus-circle" size="18" strokeWidth="2"/>
                    </A>
                </div>
            {/if}
            {#if user.canViewCalendar()}
                <div class="content-item border-left" title="daily transfers">
                    <A href="/app/calendar/{ getDate() }" class="item-button">
                        <AppIcon icon="calendar" size="18"/>
                    </A>
                </div>
            {/if}
            <div class="content-item dropdown-container border-left" on:click="{ toggleUserDropdown }">
                <div class="user">
                    { user.acronym() }
                </div>
                {#if showUserDropdown}
                    <ul class="dropdown right" use:clickOutside
                        on:click_outside="{ closeUserDropdown }">
                        <li class="p-3">
                            <div class="fs-6">{ $user.name }</div>
                            <div class="text-muted">{ $user.email }</div>
                        </li>
                        <li class="separator"></li>
                        <li class="dropdown-item pointer" on:click="{ logOut }">
                            Log out
                        </li>
                    </ul>
                {/if}
            </div>
        </div>
    </div>
</header>

<style>
    header {
        position: fixed;
        display: flex;
        align-items: center;
        height: var(--header-height);
        background: #fff;
        border-bottom: 1px solid #c6d2d9;
        width: 100%;
        z-index: 9000;
    }

    header .content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    header .menu-icon {
        color: #333;
        padding: 5px 10px;
        cursor: pointer;
    }

    @media (min-width: 992px) and (max-width: 1024px) {
        header .menu-icon {
            margin-left: 7px;
        }
    }

    @media (min-width: 1025px) {
        header .menu-icon {
            display: none;
        }
    }

    .content-left, .content-right {
        display: flex;
        align-items: stretch;
        margin-right: 15px;
    }

    header .content-left .brand {
        position: relative;
        height: 35px;
        padding: 0 10px;
    }

    header .content-left .brand img {
        height: 100%;
    }

    header .content-left .brand .version {
        position: absolute;
        font-size: 8px;
        right: 10px;
        bottom: -8px;
    }

    .content-item {
        display: flex;
        align-items: center;
        padding: 0 8px;
    }

    .content-item:last-of-type {
        padding-right: 0;
    }

    .content-item.border-left {
        border-left: 1px dotted #c6d2d9;
    }

    .content-item.border-right {
        border-right: 1px dotted #c6d2d9;
    }

    :global(.content-item .item-button) {
        position: relative;
        display: block;
        color: #90a4ae;
        padding: 7px 10px;
        cursor: pointer;
    }

    :global(.content-item .item-button .badge) {
        position: absolute;
        right: 2px;
        bottom: 2px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 13px;
        height: 13px;
        font-size: 0.6rem;
        line-height: 0;
        background: #e66465;
        border-radius: 50%;
    }

    /*:global(.content-item.item-button>a) {*/
    /*    color: #90a4ae;*/
    /*}*/

    header .content-right .user {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        color: #fff;
        background: #ffc107;
        border-radius: 50%;
        padding: 0 5px;
        margin-left: 8px;
        cursor: pointer;
    }

    @media (max-width: 320px) {
        .content-item {
            padding: 0;
        }

        /*:global(.content-item .item-button) {*/
        /*    !*padding: 7px 1px;*!*/
        /*}*/
    }

    @media (min-width: 321px) and (max-width: 375px) {
        .content-item {
            padding: 0 5px;
        }

        /*:global(.content-item .item-button) {*/
        /*    !*padding: 7px 1px;*!*/
        /*}*/
    }

    @media (min-width: 768px) and (max-width: 991px) {
    }

    @media (min-width: 992px) {

    }

    @media (max-width: 576px) {
        header .content-left .brand {
            padding: 2px 0;
        }
    }

    @media (min-width: 577px) {
        .content-item {
            padding: 0 5px;
        }
    }

    @media (min-width: 577px) and (max-width: 767px) {

    }

    @media (min-width: 768px) and (max-width: 991px) {

    }

    @media (min-width: 992px) and (max-width: 1199px) {
    }

    @media (min-width: 1200px) {
        header .content-left .brand {
            margin-left: 10px;
        }
    }
</style>